/**
 * WS4 Captures content — typed capture cards (whiteboard photos and
 * screenshots) auto-saved from the recorded WS4 session.
 *
 * Ported from:
 *   - `_legacy/ws4-assetlocker.js` lines 994-1283 — vanilla
 *     `#capturesModal` markup. The legacy modal carried 6 hand-drawn
 *     SVG "whiteboard" cards (`cap-pane-boards`) plus 6 "screenshot"
 *     cards (`cap-pane-screenshots`). We collapse both surfaces into a
 *     single ordered grid keyed by timestamp, since the React rebuild
 *     replaces the tabbed shell with a flat 3-col gallery + lightbox.
 *     The two-tag distinction (WHITEBOARD vs SCREEN) survives via the
 *     `kind` field.
 *
 * The map is keyed by workshop id so future workshops can register
 * their own captures bundles. Today only WS4 is populated; consumers
 * should treat a missing key as "no captures registered".
 */

/** Accent slot — drives the capture-card thumbnail tint and tag colour. */
export type CaptureAccent =
  | "true-blue"
  | "sky"
  | "gold"
  | "green"
  | "melon"
  | "red";

export type CaptureKind = "whiteboard" | "screen";

export type CaptureCard = {
  id: string;
  title: string;
  /** Activity / session label, e.g. "Activity 2.1", "Opening", "Close". */
  activity: string;
  /** mm:ss or h:mm marker, e.g. "00:48", "02:30". */
  timestamp: string;
  kind: CaptureKind;
  accent: CaptureAccent;
  /** Source label, e.g. "Saved", "Auto-captured". */
  source: "Saved" | "Auto-captured";
  /** One-line caption shown in the lightbox under the thumbnail. */
  caption: string;
};

export type WorkshopCaptures = {
  /** Headline summary, e.g. "12 CAPTURES · 14 MAR 2026". */
  summary: string;
  /** Footer summary, e.g. "6 WHITEBOARDS · 6 SCREENSHOTS · 14 MAR 2026". */
  footerSummary: string;
  captures: CaptureCard[];
};

const WS4_CAPTURES: WorkshopCaptures = {
  summary: "12 CAPTURES · 14 MAR 2026",
  footerSummary: "6 WHITEBOARDS · 6 SCREENSHOTS · 14 MAR 2026",
  captures: [
    // -------------------- Whiteboards --------------------
    {
      id: "wb-role-mapping",
      title: "Role Mapping — Group A",
      activity: "Activity 2.1",
      timestamp: "00:48",
      kind: "whiteboard",
      accent: "true-blue",
      source: "Saved",
      caption:
        "RACI grid output — execution owner, accountability owner, and compliance owner mapped per task. Whiteboard photo from Group A.",
    },
    {
      id: "wb-mode-matrix",
      title: "Mode Assignment Matrix — Draft",
      activity: "Activity 2.2",
      timestamp: "01:38",
      kind: "whiteboard",
      accent: "sky",
      source: "Saved",
      caption:
        "Draft In/Above/On/Out-of-Loop assignments with exception triggers. Pre-graduation policy version.",
    },
    {
      id: "wb-stress-1-2",
      title: "Stress Test — Scenarios 1 & 2 Mitigations",
      activity: "Activity 2.3",
      timestamp: "02:30",
      kind: "whiteboard",
      accent: "red",
      source: "Saved",
      caption:
        "Threshold-drift mitigation plus dual sign-off and audit log requirements. Social post + breaking news hold window noted.",
    },
    {
      id: "wb-stress-3",
      title: "Stress Test — Scenario 3 · Automation Bias",
      activity: "Activity 2.3",
      timestamp: "02:52",
      kind: "whiteboard",
      accent: "melon",
      source: "Saved",
      caption:
        "Red-team injection (~2%) and disclosure stance. Marcus W.'s \"In-Loop ≠ meaningful review\" framing captured.",
    },
    {
      id: "wb-graduation",
      title: "Mode Graduation Policy — John C.",
      activity: "Activity 2.2",
      timestamp: "01:54",
      kind: "whiteboard",
      accent: "green",
      source: "Saved",
      caption:
        "6 months clean On-the-Loop → eligible for Out-of-Loop. Ops + governance sign-off required. Annual review cadence.",
    },
    {
      id: "wb-escalation",
      title: "Escalation Triggers — Group B (6 Defined)",
      activity: "Activity 2.3",
      timestamp: "03:29",
      kind: "whiteboard",
      accent: "gold",
      source: "Saved",
      caption:
        "Confidence < threshold, novel input, regulatory flag, precedent contradiction, explicit uncertainty, time-sensitive external action.",
    },

    // -------------------- Screenshots --------------------
    {
      id: "ss-brief",
      title: "Workshop Brief — Jordan K. Screen Share",
      activity: "Opening",
      timestamp: "00:11",
      kind: "screen",
      accent: "true-blue",
      source: "Auto-captured",
      caption:
        "Opening slide deck from Jordan K. Workshop scope, objectives, and the four-mode framing introduced.",
    },
    {
      id: "ss-brief-2-1",
      title: "Group Brief — Activity 2.1 Instructions",
      activity: "Activity 2.1",
      timestamp: "00:22",
      kind: "screen",
      accent: "gold",
      source: "Auto-captured",
      caption:
        "Briefing slide for Role & Responsibility mapping exercise. Group split and time-box defined.",
    },
    {
      id: "ss-matrix-live",
      title: "Mode Assignment Matrix — Live View",
      activity: "Activity 2.2",
      timestamp: "01:54",
      kind: "screen",
      accent: "sky",
      source: "Auto-captured",
      caption:
        "Live collaborative matrix view during Activity 2.2. Mode assignments and exception triggers being filled in.",
    },
    {
      id: "ss-threshold-flag",
      title: "XAES-AI Risk Flag — Threshold Drift",
      activity: "Activity 2.3",
      timestamp: "02:22",
      kind: "screen",
      accent: "red",
      source: "Auto-captured",
      caption:
        "XAES-AI surfaces threshold drift as one of the top 3 root causes in deployment post-mortems. Priya S. moves to dual sign-off.",
    },
    {
      id: "ss-summary",
      title: "Session Summary Slide — Jordan K.",
      activity: "Close",
      timestamp: "03:38",
      kind: "screen",
      accent: "true-blue",
      source: "Auto-captured",
      caption:
        "Closing summary slide. Charter language confirmed, WS5 date noted, bundle distribution timeline.",
    },
    {
      id: "ss-action-log",
      title: "XAES-AI Action Log — Final Compilation",
      activity: "Close",
      timestamp: "03:39",
      kind: "screen",
      accent: "green",
      source: "Auto-captured",
      caption:
        "Final compiled XAES-AI action log surfaced for review before session close. All [ACTION] tags consolidated.",
    },
  ],
};

/** Workshop id → captures bundle. */
export const capturesByWorkshop: Record<string, WorkshopCaptures> = {
  WS4: WS4_CAPTURES,
  ws4: WS4_CAPTURES,
};

export function getCaptures(workshopId: string): WorkshopCaptures | null {
  return capturesByWorkshop[workshopId] ?? null;
}
